import { Injectable } from '@angular/core';
import { AuthConfig, OAuthErrorEvent, OAuthService, OAuthSuccessEvent, UserInfo } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { PrivateService } from './private.service';
import jwt_decode from "jwt-decode";


@Injectable({
  providedIn: 'root'
})
export class WipoAuthService {
  refreshTimeout = environment.refreshTimeout;
  private defaultLanguage = 'en';
  private loginUsername = '';
  private loginUserEmail = '';
  private userProfile: UserInfo;
  private _officeConfigCache: Map<string, any> = new Map<string, any>();
  private authConfig: AuthConfig = {
    // Url of the Identity Provider
    issuer: environment.oidcIssuer,
    // The SPA's id. The SPA is registerd with this id at the auth-server
    clientId: environment.oidcClientId,
    // URL of the SPA to redirect the user to after login
    redirectUri: environment.oidcRedirectUri,
    silentRefreshRedirectUri: environment.oidcSilentRedirectUri,
    postLogoutRedirectUri: environment.oidcPostLogoutRedirectUri,
    scope: environment.oidcScope
  };

  private authenticated = false;

  private _loginChangedSubject = new BehaviorSubject<boolean>(false);
  private _login2FaChangedSubject = new BehaviorSubject<boolean>(false);
  authStatusObservable = this._loginChangedSubject;
  login2FaChanged = this._login2FaChangedSubject;

  constructor(
    private oauthService: OAuthService,
    private _privateService: PrivateService,
    _cookieService: CookieService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    // override silent refresh frequency
    if (_cookieService.get('refresh-timeout')) {
      this.refreshTimeout = +_cookieService.get('refresh-timeout');
    }
  }
  silentRefresh(): void {
    this.oauthService.silentRefresh().then(event => {
       new Date(this.oauthService.getAccessTokenExpiration());
    }, (e) => {
       console.error('wipoAuthService: silent refresh failed', e);
    });
  }

  private async setAuthenticatedMode(loa: string): Promise<void> {
    this.authenticated = true;
    this._loginChangedSubject.next(true);
    this._login2FaChangedSubject.next(loa === '3');
  }

  public get username() {
    if (!this.authenticated) {
      return '';
    } else {
      return this.loginUsername;
    }
  }


  public get preferedLangague() {

    if (!this.authenticated) {
      return '';
    } else {
      return this.defaultLanguage;
    }
  }

  public get useremail() {
    if (!this.authenticated) {
      return '';
    } else {
      // this.loginUserEmail =  this.userProfile.email;
      return this.loginUserEmail;
    }
  }

  private setUnauthenticatedMode(): void {
    this.authenticated = false;
    this._loginChangedSubject.next(false);
    this._login2FaChangedSubject.next(false);
  }

  logout(): void {
    this.oauthService.logOut();
  }

  loginWithUserPassword(state?: string): void {
    this.oauthService.customQueryParams = { loa: '2' }; // 2 = basic authentication, user and password
    this.oauthService.initImplicitFlow(state);
  }

  loginWith2FA(state?: string): void {
    this.oauthService.customQueryParams = { loa: '3' }; // 3 = 2FA
    this.oauthService.initImplicitFlow(state);
  }

  isUserAuthenticated(): boolean {
    return this.authenticated;
  }

  // entry point
  initialize(): Promise<void> {
    console.debug('Authenticated', this.isUserAuthenticated());
    return new Promise(async (resolve) => {
      // init
      this.watchSilentRefresh();
      this.oauthService.configure(this.authConfig);
      this.oauthService.timeoutFactor = this.refreshTimeout;
      this.oauthService.setupAutomaticSilentRefresh();
      // try login
      const status = await this.oauthService.loadDiscoveryDocumentAndTryLogin().catch(error => console.error(error));
      
      try {
        if (status) {
          new Date(this.oauthService.getAccessTokenExpiration());
          this.refreshIdentityClaimsVariables();
          const isAuthenticated = await this.validateAuthenticationMethod();
          // this.setAuthenticatedMode('2');
          if (isAuthenticated && this.oauthService.state) {
            console.log('wipoAuthService: SUCCESS, REDIRECT:', this.oauthService.state);
            console.log(window.location.origin + this.oauthService.state);

            this.router.navigateByUrl(this.oauthService.state);
          }
        } else {
          this.setUnauthenticatedMode();
        }
      } catch (error) {
        this.setUnauthenticatedMode();
      }
      resolve();
    });
  }

  async validateAuthenticationMethod(stateUrl?: string): Promise<boolean> {
    let routeUrl = stateUrl || this.oauthService.state;
    
    const officeName = this._getOfficeNameFromURL(routeUrl);
    
    let loa = this._getLoaFromUserToken();

    if (!loa) {
      return false;
    }

    if (officeName && loa === '2') {
      let officeConfig: any = await this._getOfficeConfiguration(officeName);
      if (officeConfig.mfaEnabled) {
        console.debug('requesting 2FA ...');
        this.loginWith2FA(routeUrl);
        // Block navigation — user must complete MFA before accessing the page
        return false;
      }
    }

    this.setAuthenticatedMode(loa);

    return true;
  }

  private async _getOfficeConfiguration(officeName: string): Promise<any> {
    let officeConfig: any;

    if (this._officeConfigCache.has(officeName)) {
      officeConfig = this._officeConfigCache.get(officeName);
    } else {
      officeConfig = await this._privateService.getOfficeConfiguration(officeName).toPromise();
    }

    return officeConfig;
  }

  private _getLoaFromUserToken(): string {
    if (this.oauthService.hasValidAccessToken()) {
      let decodedJWT: any = jwt_decode(this.oauthService.getAccessToken());
      let authLevel = decodedJWT.auth_level;

      console.debug('Decoded JWT:', decodedJWT);
      console.debug('Auth Level:', authLevel);

      switch (authLevel) {
        case 1:
          return '2'; // 2FA required
        case 3:
          return '3'; // 2FA completed
        default:
          return; 
      }
    }
  }

  private _getOfficeNameFromURL(url: string): string {
    const pathSegments = url.split('/').filter(segment => segment !== '');
    
    // Get the fourth segment (index 3 after filtering empty segments)
    if (pathSegments.length >= 4) {
      return pathSegments[3]; 
    }
    
  }

  private refreshIdentityClaimsVariables() {
    const claims = this.oauthService.getIdentityClaims();
    if (claims) {
      this.loginUsername = claims['given_name'] + ' ' + claims['family_name'];
      this.loginUserEmail = claims['email'];
      this.defaultLanguage = claims['locale'];
      
    }
  }

  private watchSilentRefresh(): void {
    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        if (event.type === 'silent_refresh_error') {
          this.setUnauthenticatedMode();
          this.loginWithUserPassword(this.router.url);
        }
      } else if (event instanceof OAuthSuccessEvent && event.type === 'silently_refreshed') {
        new Date(this.oauthService.getAccessTokenExpiration());
      }
    });
  }
}
